import { defineStore } from 'pinia'
import type {
  IClientInterface,
  IIdentityChangePasswordData,
  IIdentityFetchResponse,
  IIdentityLoginData,
  IIdentityLoginResponse,
  IIdentityRegisterData,
  IIdentityRegisterResponse,
  IIdentityRegistrationCheckClient,
  IIdentityRegistrationRequest,
  IIdentityResetPasswordData,
  IIdentityState,
  IIdentityUpdateContactInfoData,
} from '~/types/auth/identityTypes'
import formatBalanceHistoryItems from '~/utils/balance/formatBalanceHistoryItems'
import type { AddressFormDataInterface } from '~/types/addressTypes'
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import { Routes } from '~/enums/Routes'
import { useOrdersContentStore } from '~/store/ordersContentStore'
import type { FrontendBalanceHistoryRecord } from '~/types/balance/balanceFrontendTypes'
import { useCurrentUser } from '~/composables/useCurrentUser'
import { fetchRetryParameters, requestHostHeaders, type RequestHostHeadersType } from '~/composables/constants/api'

export const useIdentity = defineStore('identity', {
  state: (): IIdentityState => ({
    user: null,
  }),
  actions: {
    async getUser(): Promise<IClientInterface | null> {
      if (this.user) {
        return this.user
      }

      return await this.fetchUser()
    },
    async login(body: IIdentityLoginData): Promise<IIdentityLoginResponse> {
      return await $fetch('/api/auth/login', {
        method: 'POST',
        body,
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })
    },
    async register(body: IIdentityRegisterData): Promise<IIdentityRegisterResponse> {
      const requestResponse = await this.registrationRequest(body.email)

      if (requestResponse.found) {
        if (requestResponse.password_sent) {
          return { password_sent: true }
        }

        return { errors: { user: 'auth.forms.user_already_exists' } }
      }

      return await $fetch('/api/auth/register', {
        method: 'POST',
        body,
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })
    },
    async registrationRequest(email: string): Promise<IIdentityRegistrationRequest> {
      const response = await $fetch<IIdentityRegistrationCheckClient>('/api/auth/register/check-client', {
        method: 'POST',
        body: { email },
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      const results = { found: false, password_sent: false }
      if (response.found) {
        if (response.new_password_has_been_sent) {
          results.password_sent = true
        }
        results.found = true
      }

      return results
    },
    async passwordResetRequest(email: string) {
      return await $fetch('/api/auth/request-password-reset', {
        method: 'POST',
        body: { email },
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })
    },
    async fetchUser(): Promise<IClientInterface | null> {
      this.user = await useCurrentUser().fetch()

      return this.user
    },
    async refresh(): Promise<IIdentityFetchResponse> {
      const response: IIdentityFetchResponse = await $fetch('/api/auth/refreshCurrentUser', {
        method: 'POST',
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      if (!response.user) {
        this.user = null
      } else {
        this.user = response.user
      }

      return response
    },
    async resetPassword(body: IIdentityResetPasswordData) {
      return await $fetch<{ success: boolean }>('/api/auth/password-reset', { method: 'POST', body })
    },
    async getBalanceHistory() {
      const response = await $fetch<FrontendBalanceHistoryRecord[]>('/api/balance/get-history', {
        method: 'POST',
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      return formatBalanceHistoryItems(response)
    },
    async updateContactInformation(body: IIdentityUpdateContactInfoData) {
      const user = await this.getUser()
      if (!user) return

      await $fetch('/api/auth/update-contact-info', {
        method: 'POST',
        body,
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })
    },
    async changePassword(body: IIdentityChangePasswordData) {
      return await $fetch('/api/auth/change-password', {
        method: 'POST',
        body,
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })
    },
    async logout() {
      const route = useRoute()

      const orderStore = useOrdersContentStore()
      await $fetch('/api/auth/logout', {
        method: 'POST',
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      this.user = null
      orderStore.$reset()

      if (
        (route.meta.middleware?.length ?? 0) > 1
          ? route.meta.middleware?.includes('auth')
          : route.meta.middleware === 'auth'
      ) {
        return navigateTo(getLocalizedRoute(Routes.Index), {
          redirectCode: 301,
        })
      }

      return true
    },
    async getSubscriptionStatus() {
      const user = await this.getUser()
      if (!user) return false

      const response = await $fetch('/api/auth/get-subscription-status', {
        method: 'POST',
        body: { email: user.email },
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      return response.data.result ?? false
    },
    async updateSubscriptionStatus(value: boolean) {
      const user = await this.getUser()
      if (!user) return false

      await $fetch('/api/auth/update-subscription-status', {
        method: 'POST',
        body: { subscribed: value },
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      // TODO update subscription should return update status and getSubscriptionStatus method should not be called again.
      return await this.getSubscriptionStatus()
    },
    // TODO addresses functionality should be moved outside identity store since it has nothing to do with it.
    async addDeliveryAddress(body: AddressFormDataInterface) {
      const resp = await $fetch('/api/account/add-delivery-address', {
        method: 'POST',
        body,
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      this.refresh()

      return resp
    },
    async updateDeliveryAddress(body: AddressFormDataInterface) {
      return await $fetch('/api/account/update-delivery-address', {
        method: 'POST',
        body,
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })
    },
    async deleteDeliveryAddress(id: number): Promise<boolean> {
      const response = await $fetch<{ success?: boolean }>('/api/account/delete-delivery-address', {
        method: 'POST',
        body: { id },
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      return response.success ?? false
    },
  },
})
